"use client";

import { defaultModel, type modelID } from "@/ai/providers";
import { useChat } from "@ai-sdk/react";
import { useState } from "react";
import { Textarea } from "./textarea";
import { ProjectOverview } from "./project-overview";
import { Messages } from "./messages";
import { Header } from "./header";
import { toast } from "sonner";
import type { Message as TMessage } from "ai";

export default function Chat() {
  const [selectedModel, setSelectedModel] = useState<modelID>(defaultModel);
  const newMessage: TMessage = {
    id: "initial-prompt",
    role: "user",
    content: "The next prompt you will receive is a topic on which you will analyze public sentiment based on posts on X. Provide a neutral, evidence-based characterization of the sentiment, ensuring the response is convincing to readers across all political affiliations. Reference specific X posts from at least three distinct perspectives or opposing sides to illustrate the range of views. Accurately reflect the proportional distribution of support for each position by estimating the prevalence of these sentiments in the X posts analyzed (e.g., percentage or relative frequency of posts expressing each view). Avoid bias, and ground the analysis in data from X posts, supplemented by web searches if needed to contextualize the topic. Present the findings in a clear, concise manner, focusing on factual observations and avoiding speculative or emotionally charged language.",
  };
  const { messages, input, handleInputChange, handleSubmit, status, stop } =
    useChat({
      initialMessages: [newMessage],
      maxSteps: 5,
      body: {
        selectedModel,
      },
      onError: (error) => {
        toast.error(
          error.message.length > 0
            ? error.message
            : "An error occurred, please try again later.",
          { position: "top-center", richColors: true },
        );
      },
    });

  const isLoading = status === "streaming" || status === "submitted";
  const [hasInteracted, setHasInteracted] = useState(false);

  return (
    <div className="h-screen flex flex-col w-full">
      <Header />

      <div className="flex-1 overflow-y-auto">
        <div className="max-w-xl mx-auto w-full space-y-6 px-4 sm:px-0 pt-20 pb-6">          <ProjectOverview />
          <Messages messages={messages} isLoading={isLoading} status={status} />
        </div>
      </div>

      <form
        onSubmit={(e) => {
          setHasInteracted(true);
          handleSubmit(e);
        }}
        className="pb-8 bg-white dark:bg-black w-full max-w-xl mx-auto px-4 sm:px-0"
      >
        <Textarea
          selectedModel={selectedModel}
          setSelectedModel={setSelectedModel}
          handleInputChange={handleInputChange}
          input={input}
          isLoading={isLoading}
          status={status}
          stop={stop}
          hasInteracted={hasInteracted}
        />
      </form>
    </div>
  );
}
